import { useState, useEffect, useMemo } from 'react'

const useMediaQuery = query => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches)

  useEffect(() => {
    const mql = window.matchMedia(query)
    setMatches(mql.matches)
    const handler = e => setMatches(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [query])

  return matches
}

/**
 * The phone breakpoint as the stylesheet defines it — `--breakpoint-phone` in
 * assets/styles/tailwind.css — so the one place JS needs to know a breakpoint
 * reads the same number the `phone:` and `max-phone:` classes compile to, rather
 * than a copy that can drift from it.
 *
 * The fallback is not decoration. `getComputedStyle` returns an empty string for a
 * custom property whose stylesheet has not been applied — jsdom, or a render
 * before the CSS lands — and an empty token builds the query
 * `(max-width: calc( - 1px))`, which is invalid and therefore never matches. The
 * effect is silent: the phone layout simply never appears, with nothing logged.
 *
 * Read once per mount rather than on every render, which is what the inline
 * version in MasterDataTable did.
 */
const PHONE_BREAKPOINT_FALLBACK = '600px'

export const phoneViewportQuery = () => {
  const token =
    typeof window === 'undefined'
      ? ''
      : getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-phone').trim()
  // Below the breakpoint, not at it: `phone:` is min-width, so the phone range
  // ends one pixel short of it.
  return `(max-width: calc(${token || PHONE_BREAKPOINT_FALLBACK} - 1px))`
}

/** Whether the viewport is narrower than the phone breakpoint. */
export const usePhoneViewport = () => useMediaQuery(useMemo(phoneViewportQuery, []))

export default useMediaQuery
